import { ColumnsType, Group, Table } from '@/components/_common';
import {
  TransactionData,
  TransactionType,
} from '@/core/domain/financial/BankConciliation/BankConciliation.types';
import { useBankConciliationContext } from '@/pages/FinancialPages/BankConciliationPage';
import formatterPrice from '@/utils/number/formatterPrice';
import moment from 'moment';

import './style.less';
import { Button, Statistic } from 'antd';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { useDisclosure } from '@/hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import AccountReceivableCreateModal from '@/components/Financial/BillToReceive/AccountReceivableCreateModal';
import AccountPayableCreateModal from '@/components/Financial/BillToPay/AccountPayableCreateModal';

export default function TransactionsList() {
  const {
    isCredit,
    bankAccountId,
    transactionType,
    transactionData,
    conciliatesData,
    transactionSelected,
    setTransactionSelected,
  } = useBankConciliationContext();
  const [createAccountReceivableOpened, createAccountReceivableActions] =
    useDisclosure();
  const [createAccountPayableOpened, createAccountPayableActions] =
    useDisclosure();

  const transactionDataTable = transactionData?.filter((transaction) => {
    return transactionType === transaction.type;
  });

  const totalAmount = transactionData
    ? isCredit
      ? transactionData?.reduce((acc, curr) => (acc += curr?.value), 0)
      : transactionData?.reduce((acc, curr) => (acc += curr?.value), 0) * -1
    : 0;

  const columns: ColumnsType<TransactionData> = [
    {
      dataIndex: 'name',
      key: 'name',
      title: 'Descrição',
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
      ellipsis: true,
      width: 160,
    },
    {
      dataIndex: 'transactionPayDate',
      key: 'transactionPayDate',
      title: 'Dt. pagamento',
      ellipsis: true,
      width: 80,
      render: (_, row) => moment(row.payDate).format('DD/MM/YYYY'),
    },
    {
      key: 'correspondingNumber',
      title: 'Nº Correspondente',
      ellipsis: true,
      width: 100,
      render: (_, row) => {
        return (
          conciliatesData?.find(
            (transaction) => transaction.transactionId === row.transactionId,
          )?.installments.length || 0
        );
      },
    },
    {
      dataIndex: 'correspondingValue',
      key: 'correspondingValue',
      title: 'Valor',
      ellipsis: true,
      width: 100,
      render: (_, row) => {
        if (TransactionType.credit === row.type) {
          return (
            <div style={{ color: 'rgb(160, 206, 51)' }}>
              {formatterPrice(row.value)}
            </div>
          );
        }

        return (
          <div style={{ color: 'rgb(207, 74, 29)' }}>
            {formatterPrice(row.value * -1)}
          </div>
        );
      },
    },
  ];

  const handleRowSelect = (row: TransactionData) => {
    setTransactionSelected(
      conciliatesData?.find(
        (conciliate) => conciliate.transactionId === row.transactionId,
      ) ?? row,
    );
  };

  return (
    <>
      <Table
        rowKey={(row) => row.transactionId}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: [transactionSelected?.transactionId as React.Key],
          onSelect: handleRowSelect,
        }}
        pagination={false}
        columns={columns}
        loading={false}
        dataSource={transactionDataTable}
        title={() => (
          <Group position="between">
            <Statistic
              title="Valor total das transações"
              value={totalAmount}
              formatter={(value) => formatterPrice(Number(value))}
              valueStyle={{
                color:
                  transactionType === TransactionType.credit
                    ? 'rgb(160, 206, 51)'
                    : 'red',
              }}
            />

            {transactionType === TransactionType.debit && (
              <Button
                type="primary"
                onClick={createAccountPayableActions.open}
                disabled={
                  !hasPermissions([
                    Authorities.FINANCIAL_ACCOUNT_PAYABLE_GENERATE_ACCOUNTS_PAYABLE,
                  ]) || !transactionSelected?.transactionId
                }
              >
                Criar Conta a Pagar
              </Button>
            )}

            {transactionType === TransactionType.credit && (
              <Button
                type="primary"
                target="_blank"
                onClick={createAccountReceivableActions.open}
                disabled={
                  !hasPermissions([
                    Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_ADD,
                  ]) || !transactionSelected?.transactionId
                }
              >
                Criar Conta a Receber
              </Button>
            )}
          </Group>
        )}
      />

      <AccountPayableCreateModal
        open={createAccountPayableOpened}
        onCancel={createAccountPayableActions.close}
        initialData={{
          description: transactionSelected?.name,
          payDate: moment(transactionSelected?.payDate),
          value: transactionSelected?.value,
          bankAccountId,
        }}
      />

      <AccountReceivableCreateModal
        open={createAccountReceivableOpened}
        onCancel={createAccountReceivableActions.close}
        initialData={{
          description: transactionSelected?.name,
          payDate: moment(transactionSelected?.payDate),
          value: transactionSelected?.value,
          bankAccountId,
        }}
      />
    </>
  );
}
